// Authentication Diagnostic Utility
// This script helps diagnose OAuth and Supabase authentication issues

import { supabase } from '@/integrations/supabase/client';

export interface AuthDiagnosticResult {
  supabaseConnection: boolean;
  authTablesExist: boolean | null;
  authUsersCount: number | null;
  currentUser: {
    email: string | null;
    id: string | null;
  } | null;
  authSettings: {
    hasSession: boolean;
    sessionExpiry: number | null;
    userEmail: string | null;
    userConfirmedAt: string | null;
  } | null;
  apiHealthy: boolean;
  errors: string[];
}

const buildDefaultResult = (): AuthDiagnosticResult => ({
  supabaseConnection: false,
  authTablesExist: null,
  authUsersCount: null,
  currentUser: null,
  authSettings: null,
  apiHealthy: false,
  errors: [],
});

const appendUnexpectedError = (errors: string[], error: unknown, message: string) => {
  if (error instanceof Error) {
    errors.push(`${message}: ${error.message}`);
  } else {
    errors.push(message);
  }
};

export const runAuthDiagnostic = async (): Promise<AuthDiagnosticResult> => {
  const result = buildDefaultResult();

  try {
    const healthResponse = await fetch('/api/auth/health/system', {
      credentials: 'include',
    });
    if (healthResponse.ok) {
      result.apiHealthy = true;
    } else {
      result.errors.push(`Auth health endpoint returned ${healthResponse.status}`);
    }
  } catch (error) {
    appendUnexpectedError(result.errors, error, 'Auth health endpoint unreachable');
  }

  try {
    const { data, error } = await supabase.auth.getSession();
    if (error) {
      result.errors.push(`Session lookup failed: ${error.message}`);
    } else {
      result.authSettings = {
        hasSession: Boolean(data.session),
        sessionExpiry: data.session?.expires_at ?? null,
        userEmail: data.session?.user?.email ?? null,
        userConfirmedAt: data.session?.user?.email_confirmed_at ?? null,
      };
      result.supabaseConnection = true;
    }
  } catch (error) {
    appendUnexpectedError(result.errors, error, 'Failed to resolve Supabase session');
  }

  try {
    const { data, error } = await supabase.auth.getUser();
    if (error) {
      result.errors.push(`Current user lookup failed: ${error.message}`);
    } else if (data.user) {
      result.currentUser = {
        email: data.user.email ?? null,
        id: data.user.id,
      };
    }
  } catch (error) {
    appendUnexpectedError(result.errors, error, 'Failed to fetch current user');
  }

  // Client bundle is not permitted to introspect auth tables. Surface a note if deep checks are required.
  result.errors.push(
    'Deep auth diagnostics require server credentials. Run `npm run auth:diagnose` from the workspace for full coverage.',
  );

  return result;
};

export const testEmailSignup = async (email: string, password: string) => {
  try {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });

    if (error) {
      return { success: false as const, error: error.message };
    }

    return { success: true as const, data };
  } catch (error) {
    return {
      success: false as const,
      error: error instanceof Error ? error.message : 'Unknown signup error',
    };
  }
};

type OAuthProvider = 'google' | 'apple' | 'github';

export const testOAuthSignin = async (provider: OAuthProvider) => {
  try {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });

    if (error) {
      return { success: false as const, error: error.message };
    }

    return { success: true as const, data };
  } catch (error) {
    return {
      success: false as const,
      error: error instanceof Error ? error.message : 'Unknown OAuth error',
    };
  }
};

export const checkSupabaseProjectSettings = () => {
  const env = typeof window !== 'undefined' ? import.meta.env : undefined;

  return {
    projectId: env?.VITE_SUPABASE_PROJECT_ID ?? 'unknown-project',
    supabaseUrl: env?.VITE_SUPABASE_URL ?? 'unknown',
    hasAnonKey: Boolean(env?.VITE_SUPABASE_ANON_KEY),
    hasServiceKey: Boolean(env?.VITE_SUPABASE_SERVICE_ROLE_KEY),
    currentOrigin: typeof window !== 'undefined' ? window.location.origin : 'n/a',
    currentHost: typeof window !== 'undefined' ? window.location.host : 'n/a',
  };
};
